import { useState, useEffect } from 'preact/hooks';
import { superAdminFetch, superAdminPost, superAdminPut, superAdminDelete } from '../../lib/super-admin-api';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

export function SuperAdminFeatures() {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  const [newFeature, setNewFeature] = useState(null);
  const [saving, setSaving] = useState(false);

  const categories = ['core', 'reporting', 'advanced', 'integration', 'general'];

  useEffect(() => {
    fetchFeatures();
  }, []);

  async function fetchFeatures() {
    try {
      const res = await superAdminFetch(`${API_URL}/api/super-admin/features`);
      if (!res.ok) throw new Error('Failed to fetch features');

      const data = await res.json();
      setFeatures(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveFeature(feature, isNew = false) {
    setSaving(true);
    try {
      const data = {
        key: feature.key,
        name: feature.name,
        description: feature.description || '',
        category: feature.category || 'general',
        is_active: feature.is_active ? 1 : 0,
        sort_order: parseInt(feature.sort_order) || 0
      };

      const res = isNew
        ? await superAdminPost(`${API_URL}/api/super-admin/features`, data)
        : await superAdminPut(`${API_URL}/api/super-admin/features/${feature.id}`, data);

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to save feature');
      }

      setEditingFeature(null);
      setNewFeature(null);
      await fetchFeatures();
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteFeature(feature) {
    if (!confirm(`Are you sure you want to delete "${feature.name}"? This will remove it from all plans.`)) {
      return;
    }

    try {
      const res = await superAdminDelete(`${API_URL}/api/super-admin/features/${feature.id}`);

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to delete feature');
      }

      await fetchFeatures();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  }

  function startNewFeature() {
    setNewFeature({
      key: '',
      name: '',
      description: '',
      category: 'general',
      is_active: 1,
      sort_order: features.length
    });
    setEditingFeature(null);
  }

  if (loading) {
    return (
      <div class="super-admin-page">
        <div class="loading-state">
          <div class="spinner" />
          <p>Loading features...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div class="super-admin-page">
        <div class="error-state">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Group features by category
  const featuresByCategory = categories.reduce((acc, cat) => {
    acc[cat] = features.filter(f => f.category === cat);
    return acc;
  }, {});

  return (
    <div class="super-admin-page">
      <div class="page-header">
        <div>
          <h1>Features</h1>
          <p class="text-muted">Manage available features for plans</p>
        </div>
        <div class="header-actions">
          <a href="/super-admin" class="btn btn-secondary">Back to Dashboard</a>
          <a href="/super-admin/plans" class="btn btn-secondary">Manage Plans</a>
          <button class="btn btn-primary" onClick={startNewFeature}>
            Add Feature
          </button>
        </div>
      </div>

      {/* New Feature Form */}
      {newFeature && (
        <div class="card mb-4">
          <div class="card-header">
            <h2>New Feature</h2>
          </div>
          <div class="card-body">
            <FeatureForm
              feature={newFeature}
              onChange={setNewFeature}
              onSave={() => handleSaveFeature(newFeature, true)}
              onCancel={() => setNewFeature(null)}
              saving={saving}
              categories={categories}
              isNew
            />
          </div>
        </div>
      )}

      {/* Features by Category */}
      {categories.map(category => {
        const categoryFeatures = featuresByCategory[category];
        if (categoryFeatures.length === 0 && !newFeature) return null;

        return (
          <div key={category} class="card mb-4">
            <div class="card-header">
              <h2 class="category-title">
                <span class={`badge cat-${category}`}>{category}</span>
                <span class="feature-count">{categoryFeatures.length} features</span>
              </h2>
            </div>
            <div class="card-body">
              <div class="features-list">
                {categoryFeatures.map(feature => {
                  const isEditing = editingFeature?.id === feature.id;
                  const displayFeature = isEditing ? editingFeature : feature;

                  return (
                    <div key={feature.id} class={`feature-row ${isEditing ? 'editing' : ''}`}>
                      {isEditing ? (
                        <FeatureForm
                          feature={displayFeature}
                          onChange={setEditingFeature}
                          onSave={() => handleSaveFeature(displayFeature)}
                          onCancel={() => setEditingFeature(null)}
                          saving={saving}
                          categories={categories}
                        />
                      ) : (
                        <>
                          <div class="feature-info">
                            <div class="feature-header">
                              <code class="feature-key">{feature.key}</code>
                              {feature.is_active === 0 && (
                                <span class="badge badge-warning">Inactive</span>
                              )}
                            </div>
                            <h4 class="feature-name">{feature.name}</h4>
                            {feature.description && (
                              <p class="feature-desc">{feature.description}</p>
                            )}
                          </div>
                          <div class="feature-actions">
                            <button
                              class="btn btn-sm btn-secondary"
                              onClick={() => setEditingFeature({ ...feature })}
                            >
                              Edit
                            </button>
                            <button
                              class="btn btn-sm btn-danger"
                              onClick={() => handleDeleteFeature(feature)}
                            >
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      <style>{`
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-header h1 {
          margin: 0 0 0.25rem;
        }

        .header-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .card {
          background: white;
          border-radius: 12px;
          border: 1px solid #E5E7EB;
          overflow: hidden;
        }

        .card-header {
          background: #F9FAFB;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid #E5E7EB;
        }

        .card-header h2 {
          margin: 0;
          font-size: 1rem;
        }

        .card-body {
          padding: 1.5rem;
        }

        .category-title {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .feature-count {
          font-weight: 400;
          color: #6B7280;
          font-size: 0.875rem;
        }

        .features-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .feature-row {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1rem;
          border: 1px solid #E5E7EB;
          border-radius: 8px;
          transition: all 0.2s;
        }

        .feature-row:hover {
          border-color: #D1D5DB;
          background: #FAFAFA;
        }

        .feature-row.editing {
          border-color: #4F46E5;
          background: #F5F3FF;
        }

        .feature-info {
          flex: 1;
        }

        .feature-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.25rem;
        }

        .feature-key {
          background: #F3F4F6;
          padding: 0.125rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          color: #4B5563;
        }

        .feature-name {
          margin: 0 0 0.25rem;
          font-size: 1rem;
        }

        .feature-desc {
          margin: 0;
          color: #6B7280;
          font-size: 0.875rem;
        }

        .feature-actions {
          display: flex;
          gap: 0.5rem;
        }

        .badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
        }

        .badge-warning {
          background: #FEF3C7;
          color: #92400E;
        }

        .cat-core { background: #DBEAFE; color: #1E40AF; }
        .cat-reporting { background: #D1FAE5; color: #065F46; }
        .cat-advanced { background: #FEF3C7; color: #92400E; }
        .cat-integration { background: #E0E7FF; color: #3730A3; }
        .cat-general { background: #F3F4F6; color: #374151; }

        .btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          text-decoration: none;
        }

        .btn-sm {
          padding: 0.375rem 0.75rem;
          font-size: 0.875rem;
        }

        .btn-primary {
          background: #4F46E5;
          color: white;
        }

        .btn-primary:hover {
          background: #4338CA;
        }

        .btn-secondary {
          background: #F3F4F6;
          color: #374151;
        }

        .btn-secondary:hover {
          background: #E5E7EB;
        }

        .btn-danger {
          background: #FEE2E2;
          color: #DC2626;
        }

        .btn-danger:hover {
          background: #FECACA;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .text-muted {
          color: #6B7280;
        }

        .mb-4 {
          margin-bottom: 1.5rem;
        }

        .loading-state,
        .error-state {
          text-align: center;
          padding: 4rem 2rem;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #E5E7EB;
          border-top-color: #4F46E5;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        /* Form styles */
        .feature-form {
          width: 100%;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .form-group label {
          font-size: 0.75rem;
          font-weight: 500;
          color: #374151;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 0.5rem;
          border: 1px solid #D1D5DB;
          border-radius: 6px;
          font-size: 0.875rem;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #4F46E5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }

        .form-group textarea {
          min-height: 60px;
          resize: vertical;
        }

        .form-group-inline {
          flex-direction: row;
          align-items: center;
          gap: 0.5rem;
        }

        .form-group-inline input[type="checkbox"] {
          width: 16px;
          height: 16px;
        }

        .form-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
        }

        .full-width {
          grid-column: 1 / -1;
        }
      `}</style>
    </div>
  );
}

function FeatureForm({ feature, onChange, onSave, onCancel, saving, categories, isNew }) {
  return (
    <div class="feature-form">
      <div class="form-grid">
        <div class="form-group">
          <label>Key (unique identifier)</label>
          <input
            type="text"
            value={feature.key}
            onChange={(e) => onChange({ ...feature, key: e.target.value })}
            placeholder="e.g., wallchart"
            pattern="[a-z_]+"
          />
        </div>
        <div class="form-group">
          <label>Name</label>
          <input
            type="text"
            value={feature.name}
            onChange={(e) => onChange({ ...feature, name: e.target.value })}
            placeholder="e.g., Wallchart View"
          />
        </div>
        <div class="form-group">
          <label>Category</label>
          <select
            value={feature.category}
            onChange={(e) => onChange({ ...feature, category: e.target.value })}
          >
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
        <div class="form-group">
          <label>Sort Order</label>
          <input
            type="number"
            value={feature.sort_order}
            onChange={(e) => onChange({ ...feature, sort_order: e.target.value })}
          />
        </div>
        <div class="form-group full-width">
          <label>Description</label>
          <textarea
            value={feature.description}
            onChange={(e) => onChange({ ...feature, description: e.target.value })}
            placeholder="Brief description of the feature"
          />
        </div>
        <div class="form-group form-group-inline">
          <input
            type="checkbox"
            id={`active-${feature.id || 'new'}`}
            checked={feature.is_active === 1 || feature.is_active === true}
            onChange={(e) => onChange({ ...feature, is_active: e.target.checked ? 1 : 0 })}
          />
          <label htmlFor={`active-${feature.id || 'new'}`}>Active</label>
        </div>
      </div>
      <div class="form-actions">
        <button
          class="btn btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
        <button
          class="btn btn-primary"
          onClick={onSave}
          disabled={saving || !feature.key || !feature.name}
        >
          {saving ? 'Saving...' : isNew ? 'Create Feature' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
